import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Box, Button, Card, CardBody, CardHeader, Flex, Heading, HStack, Text, VStack, useDisclosure,
} from '@chakra-ui/react';
import { FiEdit3 } from 'react-icons/fi';
import { formatCallTime, useCallTimezone } from '../../../utils/callTimezone';
import { CorrectionsAPI } from '../../../services/api';
import CorrectionEditor from './CorrectionEditor';
import { STATUS_COLORS, statusLabel } from './correctionReview';
import { correctableTurns, describeIssue, turnKey, typeLabel } from './correctionTypes';

// turnBadges(turn) → [{ label, color }]: optional notes shown under a sentence (e.g. the chat
// output guard's "已自动改写" marker). turnExtra(turn) → optional element under the sentence (e.g. a
// website chat visitor's voice message or image).
export default function CorrectionPanel({ recordRef, messages, source = 'call', lang = 'zh', turnBadges, turnExtra, includeAllMessages = false }) {
  const zh = lang === 'zh';
  const correctable = correctableTurns(messages);
  const turns = includeAllMessages ? (messages || []).map((message, index) => ({ ...message, index })) : correctable;
  const correctableKeys = new Set(correctable.map(turnKey));
  const [displayTimezone] = useCallTimezone();
  const [corrections, setCorrections] = useState([]);
  const [editing, setEditing] = useState({ turn: null, existing: null });
  const editor = useDisclosure();

  const load = useCallback(async () => {
    if (!recordRef) return;
    try {
      const page = await CorrectionsAPI.list({ record_ref: recordRef, page_size: 100 });
      setCorrections(page.items || []);
    } catch {
      setCorrections([]);
    }
  }, [recordRef]);
  useEffect(() => { load(); }, [load]);

  if (!turns.length) return null;
  const byTurn = corrections.reduce((acc, c) => ({ ...acc, [c.turn_ref]: [...(acc[c.turn_ref] || []), c] }), {});
  const open = (turn, existing = null) => { setEditing({ turn, existing }); editor.onOpen(); };

  return (
    <Card mt={includeAllMessages ? 0 : 6} minW={0} className={includeAllMessages ? 'chat-timeline' : undefined}>
      <CardHeader>
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <Heading size="md">{includeAllMessages ? (zh ? '对话记录' : 'Conversation') : (zh ? '逐句纠错' : 'Correct sentence by sentence')}</Heading>
          {corrections.length > 0 && <Badge colorScheme="green">{zh ? `已纠错 ${corrections.length} 处` : `${corrections.length} corrected`}</Badge>}
        </Flex>
        <Text fontSize="sm" color="gray.500">
          {zh ? '发现哪句话有问题，就点那句话旁边的“纠错”。没有问题的不用处理。' : 'Click "Correct" next to a sentence that went wrong. Leave the rest.'}
        </Text>
      </CardHeader>
      <CardBody>
        <VStack align="stretch" spacing={2} maxH={includeAllMessages ? undefined : '60vh'} overflowY={includeAllMessages ? undefined : 'auto'}>
          {turns.map((turn) => {
            const saved = byTurn[turnKey(turn)] || [];
            return (
              <Box key={turnKey(turn)} p={3} rounded="md" bg={turn.role === 'user' ? 'gray.50' : turn.role === 'staff' ? 'teal.50' : 'blue.50'}
                borderLeft="3px solid" borderColor={saved.length ? 'green.400' : 'transparent'}>
                <Flex justify="space-between" gap={3} align="flex-start">
                  <Box flex="1" minW={0}>
                    <Text fontSize="xs" color="gray.500" mb={1}>{turn.role === 'user' ? (zh ? '客户' : 'Customer') : turn.role === 'staff' ? `${zh ? '团队成员' : 'Team member'}${turn.staff_name ? ` · ${turn.staff_name}` : ''}` : turn.role === 'system' ? (zh ? '系统提示' : 'System notice') : 'AI'}{includeAllMessages && turn.timestamp ? ` · ${formatCallTime(turn.timestamp, displayTimezone)}` : ''}{includeAllMessages && turn.held ? (zh ? ' · 等待人工' : ' · waiting for a person') : ''}</Text>
                    {turnExtra ? turnExtra(turn) : null}
                    <Text fontSize="sm" whiteSpace="pre-wrap" overflowWrap="anywhere">{turn.content}</Text>
                    {includeAllMessages && turn.source_text && <Text fontSize="xs" color="text.secondary">{zh ? '原文：' : 'Original: '}{turn.source_text}</Text>}
                    {includeAllMessages && turn.email_copy && <Badge colorScheme={turn.email_copy.sent ? 'green' : 'red'}>{turn.email_copy.sent ? (zh ? '邮件副本已发送' : 'Email copy sent') : (zh ? '邮件副本未发出' : 'Email copy not sent')}</Badge>}
                    {(turnBadges ? turnBadges(turn) : []).map((b) => (
                      <Badge key={b.label} mt={1} mr={1} colorScheme={b.color || 'orange'} textTransform="none">{b.label}</Badge>
                    ))}
                    {saved.map((c) => (
                      <Box key={c.id} mt={2} p={2} bg="white" rounded="md" border="1px solid" borderColor="green.200">
                        <HStack wrap="wrap" spacing={1} mb={1}>
                          {c.issues.map((issue, i) => <Badge key={i} colorScheme={c.escalated ? 'red' : 'green'} textTransform="none">{typeLabel(issue.type, lang)}</Badge>)}
                          {c.escalated && <Badge colorScheme="red" variant="outline">{zh ? '已反馈技术' : 'Sent to tech team'}</Badge>}
                          {c.status && c.status !== 'active' && <Badge colorScheme={STATUS_COLORS[c.status]} variant="subtle">{statusLabel(c.status, lang)}</Badge>}
                        </HStack>
                        {c.issues.map((issue, i) => <Text key={i} fontSize="xs" color="gray.700">{describeIssue(issue, lang)}</Text>)}
                        <Flex justify="space-between" align="center" mt={1}>
                          <Text fontSize="xs" color="gray.500">{c.updated_by_email || c.created_by_email}</Text>
                          <Button size="xs" variant="link" onClick={() => open(turn, c)}>{zh ? '修改' : 'Edit'}</Button>
                        </Flex>
                      </Box>
                    ))}
                  </Box>
                  {(!includeAllMessages || correctableKeys.has(turnKey(turn))) && <Button size="xs" leftIcon={<FiEdit3 />} variant="outline" onClick={() => open(turn)}>{zh ? '纠错' : 'Correct'}</Button>}
                </Flex>
              </Box>
            );
          })}
        </VStack>
      </CardBody>
      {editing.turn && (
        <CorrectionEditor
          isOpen={editor.isOpen} onClose={editor.onClose} onSaved={load}
          recordRef={recordRef} source={source} turn={editing.turn} existing={editing.existing} lang={lang}
        />
      )}
    </Card>
  );
}
